import { DictaphoneState } from '@components/Dictaphone/types';
import { RecordingUploadState } from '@components/Dictaphone/utils/OnlineRecordingUploader';
import { useCallback, useEffect, useRef, useState } from 'react';

export enum IndexDBNames {
    AudioDB = 'audio-db',
}

export enum IndexDBStoreNames {
    Recordings = 'recordings',
}

const DB_VERSION = 3;

/**
 * The only lifecycle states a recording row may persist. Error is intentionally not
 * persistable: it is derived on restore (from the backend status or the absence of a
 * matching backend record), while the row keeps the recoverable state restore acts on.
 */
export type PersistedRecordingState =
    | DictaphoneState.Recording
    | DictaphoneState.Paused
    | DictaphoneState.Processing
    | DictaphoneState.Completed;

export interface RecordingData {
    id: string;
    name?: string;
    searchString?: string;
    totalSeconds: number;
    createdAt: number;
    state: PersistedRecordingState;
    opfsPath?: string;
    upload?: RecordingUploadState;
}

interface UseIndexedDBProps {
    dbName: IndexDBNames;
    storeName: IndexDBStoreNames;
}

export function useIndexedDB({ dbName, storeName }: UseIndexedDBProps) {
    const [isInitialized, setIsInitialized] = useState(false);
    const [records, setRecords] = useState<RecordingData[]>([]);
    const dbRef = useRef<IDBDatabase | null>(null);

    useEffect(() => {
        const request = indexedDB.open(dbName, DB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(storeName)) {
                db.createObjectStore(storeName, { keyPath: 'id' });
            }
        };

        request.onsuccess = () => {
            dbRef.current = request.result;
            setIsInitialized(true);
        };

        request.onerror = () => {
            console.error('IndexedDB init error');
        };
    }, [dbName, storeName]);

    const getStore = useCallback(
        (mode: IDBTransactionMode) => {
            if (!dbRef.current) {
                throw new Error('IndexedDB not initialized');
            }

            return dbRef.current.transaction(storeName, mode).objectStore(storeName);
        },
        [storeName],
    );

    const getAll = useCallback(async () => {
        return new Promise<void>(resolve => {
            const req = getStore('readonly').getAll();
            req.onsuccess = () => {
                setRecords(req.result);
                resolve();
            };
        });
    }, [getStore]);

    const add = useCallback(
        async (data: RecordingData) => {
            return new Promise<void>(resolve => {
                const req = getStore('readwrite').add(data);
                req.onsuccess = () => resolve();
            });
        },
        [getStore],
    );

    const update = useCallback(
        async (id: string, patch: Partial<RecordingData>) => {
            return new Promise<void>((resolve, reject) => {
                const store = getStore('readwrite');
                const getReq = store.get(id);

                getReq.onerror = () => reject(getReq.error);
                getReq.onsuccess = () => {
                    // The record can be deleted while an upload part is in flight —
                    // there is nothing left to update then.
                    if (!getReq.result) {
                        resolve();
                        return;
                    }
                    const putReq = store.put({ ...getReq.result, ...patch });
                    putReq.onsuccess = () => resolve();
                    putReq.onerror = () => reject(putReq.error);
                };
            });
        },
        [getStore],
    );

    const updateState = useCallback(
        async (id: string, state: PersistedRecordingState) => update(id, { state }),
        [update],
    );

    const updateTotalSeconds = useCallback(
        async (id: string, totalSeconds: number) => update(id, { totalSeconds }),
        [update],
    );

    const remove = useCallback(
        async (id: string) => {
            return new Promise<void>(resolve => {
                getStore('readwrite').delete(id).onsuccess = () => resolve();
            });
        },
        [getStore],
    );

    const getById = useCallback(
        async (id: string) => {
            return new Promise<RecordingData | undefined>(resolve => {
                const req = getStore('readonly').get(id);
                req.onsuccess = () => resolve(req.result);
            });
        },
        [getStore],
    );

    return {
        isInitialized,
        records,
        getAll,
        add,
        update,
        updateState,
        updateTotalSeconds,
        remove,
        getById,
    };
}
